import { getRandomInt, shuffle } from "../Shared/Helpers.tsx";
import { ProblemData } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { problemFromFactKey } from "./FactKeys.ts";
import { ladderForSettings, levelOf, reviewFacts } from "./FactLevels.ts";

/**
 * A fact fluency level test, dealt rather than drawn (ADR 0071 §3).
 *
 * `GenerateDataV2` draws each operand at random with replacement, which is
 * fine for practice and wrong for a test: a 21-problem x7 sheet drawn that way
 * can print 7 x 8 three times and 7 x 6 never. A test exists to cover its
 * level, so here every page is dealt:
 *
 * - when the page holds at least as many problems as the level has facts,
 *   every fact appears, and repeats are spread so no fact appears twice more
 *   than another;
 * - when it holds fewer, it is a sample with no repeats;
 * - a fact never sits beside itself or its turnaround (3 x 7 beside 7 x 3),
 *   where a bounded repair can prevent it.
 *
 * A home practice sheet (ADR 0071 §8) is the same deal with no review: the
 * level's own facts only, so a child practises exactly what the test will ask
 * for next. Where a level has no review anyway (the first, and every times
 * table) the two sheets hold the same facts.
 *
 * Every page is a test of its own, so each page covers the level. All
 * randomness goes through `shuffle` and `getRandomInt`, so a level test
 * reproduces from its seed (ADR 0009).
 */
export function dealLevelPages(settings: ISettingsProps): ProblemData[][] {
    const ladder = ladderForSettings(settings);
    const level = levelOf(settings);
    if (!ladder || !level) {
        return [];
    }
    const review = settings.FactLevelSheet === 'practice' ? [] : reviewFacts(ladder, level.id);
    const perPage = settings.NumberOfProblemsPerPage;

    const pages: ProblemData[][] = [];
    for (let page = 0; page < (settings.NumberOfPages || 1); page++) {
        const focusCount = review.length === 0 ? perPage : Math.round(perPage * level.focusShare);
        const keys = separateNeighbours(
            shuffle([...deal(level.focus, focusCount), ...deal(review, perPage - focusCount)])
        );
        pages.push(
            keys
                .map((key, index) => problemFromFactKey(key, index + 1))
                .filter((problem): problem is ProblemData => problem !== null)
        );
    }
    return pages;
}

/**
 * `count` facts from `facts`: whole shuffled passes through the list, then part
 * of one more. That is what spreads repeats evenly - no fact can come round a
 * second time until every fact has come round once.
 */
export function deal(facts: string[], count: number): string[] {
    const dealt: string[] = [];
    while (dealt.length < count && facts.length > 0) {
        dealt.push(...shuffle(facts).slice(0, count - dealt.length));
    }
    return dealt;
}

/** The same fact the other way round, for the two operations where that is the same fact. */
export function turnaround(key: string): string | null {
    const match = key.match(/^(\d+)([+x])(\d+)$/);
    return match ? `${match[3]}${match[2]}${match[1]}` : null;
}

function clash(a: string | undefined, b: string | undefined): boolean {
    if (a === undefined || b === undefined) {
        return false;
    }
    return a === b || turnaround(a) === b;
}

function clashesAround(keys: string[], index: number): number {
    return (clash(keys[index - 1], keys[index]) ? 1 : 0) + (clash(keys[index], keys[index + 1]) ? 1 : 0);
}

/**
 * Moves apart any fact that sits beside itself or its turnaround, by swapping
 * it with another position where the swap leaves both places clean. Bounded:
 * a level of seven doubles on 21 problems may leave no clean swap, and a page
 * with one neighbouring pair is better than a short page.
 */
export function separateNeighbours(keys: string[]): string[] {
    const result = [...keys];
    for (let pass = 0; pass < 10; pass++) {
        let clean = true;
        for (let i = 1; i < result.length; i++) {
            if (!clash(result[i - 1], result[i])) {
                continue;
            }
            clean = false;
            const start = getRandomInt(0, result.length - 1);
            for (let step = 0; step < result.length; step++) {
                const j = (start + step) % result.length;
                if (j === i) {
                    continue;
                }
                [result[i], result[j]] = [result[j], result[i]];
                if (clashesAround(result, i) + clashesAround(result, j) === 0) {
                    break;
                }
                [result[i], result[j]] = [result[j], result[i]];
            }
        }
        if (clean) {
            break;
        }
    }
    return result;
}
